/**
 * Contact Page - Lead qualification form with Formspree
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  AppBar,
  Toolbar,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Send as SendIcon
} from '@mui/icons-material';

interface ContactPageProps {
  onBack: () => void;
}

const ContactPage: React.FC<ContactPageProps> = ({ onBack }) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://formspree.io/f/myzkpzdv', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (err) {
      setError('There was an error submitting the form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #003399 0%, #0052cc 100%)' }}>
      <AppBar position="static" sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
        <Toolbar>
          <Button
            startIcon={<BackIcon />}
            onClick={onBack}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Back
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper
          elevation={8}
          sx={{
            p: 6,
            background: 'rgba(255, 255, 255, 0.98)',
            borderRadius: 4
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: '#003399',
              mb: 2,
              textAlign: 'center'
            }}
          >
            Contact Us
          </Typography>
          
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ mb: 2, textAlign: 'center' }}
          >
            Tell us about your Erasmus+ project needs and we'll get back to you shortly.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            Consulta la nostra{' '}
            <Link href="?page=privacy-policy" sx={{ color: '#003399', fontWeight: 600 }}>
              Informativa Privacy
            </Link>
            .
          </Typography>

          {submitted ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Thank you for your interest!
              </Typography>
              <Typography variant="body2">
                We've received your message and will get back to you within 24 hours.
              </Typography>
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Personal Info */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    name="name"
                    label="Full Name"
                    placeholder="John Smith"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    name="email"
                    type="email"
                    label="Email Address"
                    placeholder="john@organization.com"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="organization"
                    label="Organization Name"
                    placeholder="Your organization"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="country"
                    label="Country"
                    placeholder="e.g. Italy, Spain, Germany"
                  />
                </Grid>

                {/* Qualification Fields */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    name="organization_type"
                    label="Organization Type"
                    defaultValue=""
                  >
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value="school">School (K-12)</MenuItem>
                    <MenuItem value="university">University / Higher Education</MenuItem>
                    <MenuItem value="vet">VET Provider</MenuItem>
                    <MenuItem value="adult_education">Adult Education Center</MenuItem>
                    <MenuItem value="ngo">NGO / Non-Profit</MenuItem>
                    <MenuItem value="public_body">Public Body / Municipality</MenuItem>
                    <MenuItem value="sme">SME / Company</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    name="erasmus_experience"
                    label="Erasmus+ Experience"
                    defaultValue=""
                  >
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value="none">No previous experience</MenuItem>
                    <MenuItem value="1-2">1-2 projects</MenuItem>
                    <MenuItem value="3-5">3-5 projects</MenuItem>
                    <MenuItem value="5+">More than 5 projects</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    name="project_type"
                    label="Project Type of Interest"
                    defaultValue=""
                  >
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value="KA1">KA1 - Mobility</MenuItem>
                    <MenuItem value="KA2-SCH">KA2 - School Education</MenuItem>
                    <MenuItem value="KA2-VET">KA2 - Vocational Training</MenuItem>
                    <MenuItem value="KA2-HED">KA2 - Higher Education</MenuItem>
                    <MenuItem value="KA2-ADU">KA2 - Adult Education</MenuItem>
                    <MenuItem value="not_sure">Not sure yet</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    name="timeline"
                    label="When do you plan to apply?"
                    defaultValue=""
                  >
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value="next_deadline">Next deadline</MenuItem>
                    <MenuItem value="6_months">Within 6 months</MenuItem>
                    <MenuItem value="1_year">Within 1 year</MenuItem>
                    <MenuItem value="exploring">Just exploring</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    name="message"
                    label="Tell us about your project idea"
                    placeholder="Describe your project idea, goals, and any questions you have..."
                  />
                </Grid>

                {/* Hidden field for form identification */}
                <input type="hidden" name="_subject" value="New Erasmus Generator Lead" />

                {error && (
                  <Grid item xs={12}>
                    <Alert severity="error">{error}</Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <SendIcon />}
                    sx={{
                      py: 1.5,
                      bgcolor: '#003399',
                      '&:hover': { bgcolor: '#002266' }
                    }}
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}

          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mt: 4, textAlign: 'center' }}
          >
            We typically respond within 24 hours during business days.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default ContactPage;
